import React, { useState } from 'react'
import { Row, Card, Form, Input, Space, Button, Col, Typography } from 'antd'
import InputMask from 'react-input-mask'
import { EmptyHeaderHero, GlobalHeader, GlobalFooter } from './components'
import { sendEmailForExpressPolicyChanges } from '../services'
import { REMOVE_VEHICLE_JSON_MAP } from './RemoveVehicleConstants'

import RemoveVehicleStyle from '../../styles/RemoveVehicle.module.css'
import { NoIndex } from './NoIndex'

const { Text } = Typography
const { TextArea } = Input

const RemoveVehicle = () => {
  const [driver, setDriver] = useState({
    firstName: '',
    lastName: '',
    policyNumber: '',
    phoneNumber: '',
    streetAddress: '',
    zipCode: '',
    emailAddress: '',
    reason: '',
    vehicleInfo: {
      vin: '',
      year: '',
      make: '',
      model: ''
    },
    effectDate: ''
  })
  const [disableButton, setDisableButton] = useState(false)

  const validateBasicFormData = driverInfo => {
    const {
      firstName,
      lastName,
      policyNumber,
      phoneNumber,
      streetAddress,
      zipCode,
      emailAddress,
      reason,
      vehicleInfo: { vin, year, make, model },
      effectDate
    } = driverInfo

    if (
      firstName &&
      lastName &&
      policyNumber &&
      phoneNumber &&
      streetAddress &&
      zipCode &&
      emailAddress &&
      reason &&
      vin &&
      year &&
      make &&
      model &&
      effectDate
    ) {
      return { isValid: true }
    } else {
      return { isValid: false }
    }
  }

  const validateDate = driverInfo => {
    const { effectDate } = driverInfo

    if (new Date(effectDate).toString() === 'Invalid Date') {
      return { isValid: false }
    } else {
      return { isValid: true }
    }
  }

  const validateEmail = driverInfo => {
    const { emailAddress } = driverInfo
    const regEx = /\S+@\S+\.\S+/

    if (emailAddress.length > 0 && regEx.test(emailAddress)) {
      return { isValid: true }
    } else {
      return { isValid: false }
    }
  }

  const validateZipCode = driverInfo => {
    const { zipCode } = driverInfo

    if (zipCode.length < 5) {
      return { isValid: false }
    } else {
      return { isValid: true }
    }
  }

  const formatPhoneNumber = phoneNumber => {
    const USNumber = phoneNumber.match(/(\d{3})(\d{3})(\d{4})/)
    return USNumber ? `(${USNumber?.[1]}) ${USNumber?.[2]}-${USNumber?.[3]}` : ''
  }

  const validatePhoneNumber = driverInfo => {
    const { phoneNumber } = driverInfo
    const updatedNumber = phoneNumber.replace(/[^\w\s]/gi, '').replace(' ', '')
    if (formatPhoneNumber(updatedNumber).length < 10) {
      return { isValid: false }
    } else {
      return { isValid: true }
    }
  }

  const validateFields = driverInfo => {
    let validationResponse = true
    const initialResponse = validateBasicFormData(driverInfo)
    if (initialResponse.isValid) {
      const dateValidationResponse = validateDate(driverInfo)
      const emailValidationResponse = validateEmail(driverInfo)
      const zipCodeValidationResponse = validateZipCode(driverInfo)
      const phoneValidationResponse = validatePhoneNumber(driverInfo)
      if (
        dateValidationResponse.isValid &&
        emailValidationResponse.isValid &&
        zipCodeValidationResponse.isValid &&
        phoneValidationResponse.isValid
      ) {
        validationResponse = true
      } else {
        validationResponse = false
      }
    } else {
      validationResponse = false
    }
    return validationResponse
  }

  const stripFinalJson = driverInfo => {
    const {
      firstName,
      lastName,
      policyNumber,
      phoneNumber,
      streetAddress,
      zipCode,
      emailAddress,
      reason,
      vehicleInfo: { vin, year, make, model },
      effectDate
    } = driverInfo

    const obj = {
      firstName,
      lastName,
      policyNumber,
      phoneNumber,
      streetAddress,
      zipCode,
      emailAddress,
      vehicleInfo: {
        vin,
        year,
        make,
        model
      },
      reason,
      effectDate
    }
    return obj
  }

  const buildTable = driverInfo => {
    var table = '<table border="1">'
    table += '<tr><td colspan="2"><b>Policyholder Information</b></td></tr>'
    for (const key in driverInfo) {
      if (driverInfo[key] instanceof Object) {
        table += '<tr><td colspan="2"><b>Vehicle Information</b></td></tr>'
        for (const additionalKey in driverInfo[key]) {
          table += `<tr><td>${REMOVE_VEHICLE_JSON_MAP[key][additionalKey]}</td><td>${driverInfo[key][additionalKey]}</td></tr>`
        }
      } else {
        table += `<tr><td>${REMOVE_VEHICLE_JSON_MAP[key]}</td><td>${driverInfo[key]}</td></tr>`
      }
    }
    table += '</table>'
    return table
  }

  const handleSubmit = async () => {
    setDisableButton(true)
    const finalJson = stripFinalJson(driver)
    const htmlText = buildTable(finalJson)
    const mailObj = {
      subject: `[Remove Vehicle] - ${driver.policyNumber}`,
      content: htmlText
    }
    localStorage.setItem('expressPolicyChangeType', 'Remove Vehicle')

    await sendEmailForExpressPolicyChanges(mailObj)
    const newWindow = window.open('/g/express-policy-changes/result', '_self', 'noopener,noreferrer')
    if (newWindow) newWindow.opener = null
  }

  const updateUserTextFields = (text, field) => {
    const clonedUser = { ...driver }
    clonedUser[field] = text
    setDriver(clonedUser)
  }

  const buttonStatus = !validateFields(driver) || disableButton

  return (
    <div className="container">
      <NoIndex/>
      <GlobalHeader />
      <>
        <EmptyHeaderHero minHeight={true} title="Remove Vehicle" />
        <div className={`${RemoveVehicleStyle["remove-vehicle"]}`}>
          <p className={`${RemoveVehicleStyle["disclaimer"]}`}>Once your change request has been approved and processed, you will be sent updated policy documents and billing schedule.</p>
          <Row>
            <Card className={`${RemoveVehicleStyle["register-card"]}`}>
              <h5 className={`${RemoveVehicleStyle["sub-label"]}`}>Policyholder Information</h5>
              <Row>
                <Col xl={{ span: 8 }} xs={{ span: 24 }}>
                  <Form.Item className={`${RemoveVehicleStyle["margin-bottom-14"]}`}>
                    <Input
                      autoComplete="off"
                      className={`${RemoveVehicleStyle["input-text"]}`}
                      onChange={event => { updateUserTextFields(event.target.value, "firstName") }}
                      placeholder="First Name*"
                      value={driver.firstName}
                    />
                  </Form.Item>
                </Col>
                <Col xl={{ span: 8, offset: 1 }} xs={{ span: 24 }}>
                  <Form.Item className={`${RemoveVehicleStyle["margin-bottom-14"]}`}>
                    <Input
                      autoComplete="off"
                      className={`${RemoveVehicleStyle["input-text"]}`}
                      onChange={event => { updateUserTextFields(event.target.value, "lastName") }}
                      placeholder="Last Name*"
                      value={driver.lastName}
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Row>
                <Col xl={{ span: 8 }} xs={{ span: 24 }}>
                  <Form.Item className={`${RemoveVehicleStyle["margin-bottom-14"]}`}>
                    <Input
                      autoComplete="off"
                      className={`${RemoveVehicleStyle["input-text"]}`}
                      onChange={event => { updateUserTextFields(event.target.value, "policyNumber") }}
                      placeholder="Policy Number*"
                      value={driver.policyNumber}
                    />
                  </Form.Item>
                </Col>
                <Col xl={{ span: 8, offset: 1 }} xs={{ span: 24 }}>
                  <Form.Item className={`${RemoveVehicleStyle["margin-bottom-14"]}`}>
                    <InputMask
                      className={`${RemoveVehicleStyle["input-text"]} ${RemoveVehicleStyle["phone-number"]}`}
                      mask="(999) 999-9999"
                      onChange={event => { updateUserTextFields(event.target.value, "phoneNumber") }}
                      placeholder="Phone Number*"
                      value={driver.phoneNumber}
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Row>
                <Col xl={{ span: 8 }} xs={{ span: 24 }}>
                  <Form.Item className={`${RemoveVehicleStyle["margin-bottom-14"]}`}>
                    <Input
                      autoComplete="off"
                      className={`${RemoveVehicleStyle["input-text"]}`}
                      onChange={event => {
                        setDriver({
                          ...driver,
                          streetAddress: event.target.value
                        })
                      }}
                      placeholder="Street Address*"
                      value={driver.streetAddress}
                    />
                  </Form.Item>
                </Col>
                <Col xl={{ span: 8, offset: 1 }} xs={{ span: 24 }}>
                  <Form.Item className={`${RemoveVehicleStyle["margin-bottom-14"]}`}>
                    <Input
                      autoComplete="off"
                      className={`${RemoveVehicleStyle["input-text"]}`}
                      maxLength={5}
                      onChange={event => {
                        setDriver({
                          ...driver,
                          zipCode: event.target.value.replace(/\D/g, '')
                        })
                      }}
                      placeholder="Zip Code*"
                      value={driver.zipCode}
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Row>
                <Col xl={{ span: 8 }} xs={{ span: 24 }}>
                  <Form.Item className={`${RemoveVehicleStyle["margin-bottom-14"]}`}>
                    <Input
                      autoComplete="off"
                      className={`${RemoveVehicleStyle["input-text"]}`}
                      onChange={event => { updateUserTextFields(event.target.value, "emailAddress") }}
                      placeholder="Email Address*"
                      value={driver.emailAddress}
                    />
                  </Form.Item>
                </Col>
              </Row>
              <h5 className={`${RemoveVehicleStyle["sub-label"]}`}>Vehicle Information</h5>
              <Row>
                <Col xl={{ span: 8 }} xs={{ span: 24 }}>
                  <Form.Item className={`${RemoveVehicleStyle["margin-bottom-14"]}`}>
                    <Input
                      autoComplete="off"
                      className={`${RemoveVehicleStyle["input-text"]}`}
                      onChange={event => {
                        const vehicleInfo = { ...driver.vehicleInfo }
                        vehicleInfo.vin = event.target.value
                        setDriver({
                          ...driver,
                          vehicleInfo
                        })
                      }}
                      placeholder="VIN*"
                      value={driver.vehicleInfo.vin}
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Row>
                <Col xl={{ span: 8 }} xs={{ span: 24 }}>
                  <Row>
                    <Col xl={{ span: 7 }} xs={{ span: 24 }}>
                      <Form.Item className={`${RemoveVehicleStyle["margin-bottom-14"]}`}>
                        <Input
                          autoComplete="off"
                          className={`${RemoveVehicleStyle["input-text"]}`}
                          onChange={event => {
                            const vehicleInfo = { ...driver.vehicleInfo }
                            vehicleInfo.year = event.target.value
                            setDriver({
                              ...driver,
                              vehicleInfo
                            })
                          }}
                          placeholder="Year*"
                          value={driver.vehicleInfo.year}
                        />
                      </Form.Item>
                    </Col>
                    <Col xl={{ span: 7, offset: 1 }} xs={{ span: 24 }}>
                      <Form.Item className={`${RemoveVehicleStyle["margin-bottom-14"]}`}>
                        <Input
                          autoComplete="off"
                          className={`${RemoveVehicleStyle["input-text"]}`}
                          onChange={event => {
                            const vehicleInfo = { ...driver.vehicleInfo }
                            vehicleInfo.make = event.target.value
                            setDriver({
                              ...driver,
                              vehicleInfo
                            })
                          }}
                          placeholder="Make*"
                          value={driver.vehicleInfo.make}
                        />
                      </Form.Item>
                    </Col>
                    <Col xl={{ span: 8, offset: 1 }} xs={{ span: 24 }}>
                      <Form.Item className={`${RemoveVehicleStyle["margin-bottom-14"]}`}>
                        <Input
                          autoComplete="off"
                          className={`${RemoveVehicleStyle["input-text"]}`}
                          onChange={event => {
                            const vehicleInfo = { ...driver.vehicleInfo }
                            vehicleInfo.model = event.target.value
                            setDriver({
                              ...driver,
                              vehicleInfo
                            })
                          }}
                          placeholder="Model*"
                          value={driver.vehicleInfo.model}
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                </Col>
              </Row>
              <Text className={`${RemoveVehicleStyle["form-effect-label"]}`}>What is the primary reason for removing this vehicle?*</Text>
              <Row>
                <Col xl={{ span: 8 }} xs={{ span: 24 }}>
                  <Form.Item className={`${RemoveVehicleStyle["margin-bottom-14"]}`}>
                    <TextArea
                      autoComplete="off"
                      className={`${RemoveVehicleStyle["textarea-text"]}`}
                      onChange={event => {
                        setDriver({
                          ...driver,
                          reason: event.target.value
                        })
                      }}
                      placeholder="Please explain why*"
                      rows={4}
                      value={driver.reason}
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Text className={`${RemoveVehicleStyle["form-effect-label"]}`}>When would you like this change to go into effect?*</Text>
              <Row>
                <Col xl={{ span: 8 }} xs={{ span: 24 }}>
                  <Form.Item className={`${RemoveVehicleStyle["margin-bottom-14"]}`}>
                    <InputMask
                      className={`${RemoveVehicleStyle["dob"]}`}
                      mask="99/99/9999"
                      onChange={event => {
                        setDriver({
                          ...driver,
                          effectDate: event.target.value
                        })
                      }}
                      placeholder="mm/dd/yyyy*"
                      value={driver.effectDate}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Card>
          </Row>
          <Row className={`${RemoveVehicleStyle["submit-container"]}`}>
            <Form.Item className={`${RemoveVehicleStyle["btn-control"]} ${RemoveVehicleStyle["button-submit-form"]}`}>
              <Space>
                <Button className={`${RemoveVehicleStyle["register"]} ${RemoveVehicleStyle["register-form-button"]}`} disabled={buttonStatus} onClick={handleSubmit}>
                  Submit
                </Button>
              </Space>
            </Form.Item>
          </Row>
        </div>
      </>
      <GlobalFooter />
    </div>
  )
}

RemoveVehicle.propTypes = {}

export default RemoveVehicle
